import { supabase } from '../supabase';

// ── Types ──

export type SsoProviderType = 'azure_ad' | 'google' | 'saml' | 'oidc';

export interface SsoProvider {
  id: string;
  name: string;
  provider_type: SsoProviderType;
  client_id: string | null;
  tenant_id: string | null;
  discovery_url: string | null;
  saml_metadata_url: string | null;
  saml_entity_id: string | null;
  enabled: boolean;
  auto_create_users: boolean;
  default_role: string;
  company_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SsoRoleMapping {
  id: string;
  provider_id: string;
  external_group: string;
  erp_role: string;
  created_at: string;
}

export interface SsoLoginLogEntry {
  id: string;
  provider_id: string | null;
  user_id: string | null;
  email: string;
  external_id: string | null;
  external_groups: string[] | null;
  mapped_role: string | null;
  success: boolean;
  error_message: string | null;
  created_at: string;
}

// ── Provider CRUD ──

export async function getSsoProviders(): Promise<SsoProvider[]> {
  const { data, error } = await supabase
    .from('sso_providers')
    .select('id, name, provider_type, client_id, tenant_id, discovery_url, saml_metadata_url, saml_entity_id, enabled, auto_create_users, default_role, company_id, created_at, updated_at')
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as SsoProvider[];
}

/**
 * What the login page needs, and exactly what the query returns. Reusing the full `SsoProvider`
 * here made TypeScript promise five fields the select never asks for — `saml_metadata_url`,
 * `saml_entity_id`, `company_id`, `created_at`, `updated_at` — all of which were `undefined` at
 * runtime (WIRING audit 2026-09-09). A narrow query gets a narrow type.
 */
export type EnabledSsoProvider = Pick<
  SsoProvider,
  'id' | 'name' | 'provider_type' | 'client_id' | 'tenant_id' | 'discovery_url' | 'enabled' | 'auto_create_users' | 'default_role'
>;

export async function getEnabledSsoProviders(): Promise<EnabledSsoProvider[]> {
  const { data, error } = await supabase
    .from('sso_providers')
    .select('id, name, provider_type, client_id, tenant_id, discovery_url, enabled, auto_create_users, default_role')
    .eq('enabled', true)
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as EnabledSsoProvider[];
}

export async function createSsoProvider(provider: Omit<SsoProvider, 'id' | 'created_at' | 'updated_at'>): Promise<string> {
  const { data, error } = await supabase
    .from('sso_providers')
    .insert({ ...provider, updated_at: new Date().toISOString() })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return data.id;
}

export async function updateSsoProvider(id: string, updates: Partial<SsoProvider>): Promise<void> {
  const { error } = await supabase
    .from('sso_providers')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteSsoProvider(id: string): Promise<void> {
  const { error } = await supabase
    .from('sso_providers')
    .delete()
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function toggleSsoProvider(id: string, enabled: boolean): Promise<void> {
  await updateSsoProvider(id, { enabled });
}

// ── Role Mapping CRUD ──

export async function getRoleMappings(providerId: string): Promise<SsoRoleMapping[]> {
  const { data, error } = await supabase
    .from('sso_role_mappings')
    .select('*')
    .eq('provider_id', providerId)
    .order('external_group');
  if (error) throw new Error(error.message);
  return (data ?? []) as SsoRoleMapping[];
}

export async function createRoleMapping(providerId: string, externalGroup: string, erpRole: string): Promise<void> {
  const { error } = await supabase
    .from('sso_role_mappings')
    .insert({ provider_id: providerId, external_group: externalGroup, erp_role: erpRole });
  if (error) throw new Error(error.message);
}

export async function deleteRoleMapping(id: string): Promise<void> {
  const { error } = await supabase
    .from('sso_role_mappings')
    .delete()
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ── SSO Login Log ──

export async function getSsoLoginLog(limit = 50): Promise<SsoLoginLogEntry[]> {
  const { data, error } = await supabase
    .from('sso_login_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as SsoLoginLogEntry[];
}

// ── SSO Sign In ──

export async function signInWithSso(providerType: SsoProviderType): Promise<void> {
  const providerMap: Record<SsoProviderType, 'azure' | 'google'> = {
    azure_ad: 'azure',
    google: 'google',
    saml: 'azure', // fallback
    oidc: 'google', // fallback
  };

  const { error } = await supabase.auth.signInWithOAuth({
    provider: providerMap[providerType],
    options: {
      redirectTo: `${window.location.origin}/`,
    },
  });
  if (error) throw new Error(error.message);
}
